/**
 * Development Error Logger
 * Formats errors in a way that's easy to copy and paste for debugging
 */

interface ErrorContext {
  component?: string;
  props?: any;
  action?: string;
  [key: string]: any;
}

class DevelopmentErrorLogger {
  private isDevelopment: boolean;

  constructor() {
    this.isDevelopment =
      process.env.NODE_ENV === "development" ||
      window.location.hostname === "localhost" ||
      window.location.hostname.includes(".lvh.me");

    if (this.isDevelopment) {
      this.setupGlobalErrorHandlers();
    }
  }

  /**
   * Log an error with context
   */
  logError(error: Error, context?: ErrorContext) {
    if (!this.isDevelopment) return;

    const errorInfo = this.formatError(error, context);

    console.group("🚨 Development Error");
    console.log(errorInfo);
    console.log("\n📋 Error report formatted for debugging");
    console.groupEnd();
  }

  /**
   * Format error for debugging
   */
  private formatError(error: Error, context?: ErrorContext): string {
    const timestamp = new Date().toISOString();
    const url = window.location.href;

    let formatted = `## Error Report

### Error Details
- **Type**: ${error.name}
- **Message**: ${error.message}
- **Timestamp**: ${timestamp}
- **URL**: ${url}
`;

    if (context) {
      formatted += `
### Context
\`\`\`json
${JSON.stringify(context, null, 2)}
\`\`\`
`;
    }

    formatted += `
### Stack Trace
\`\`\`javascript
${error.stack}
\`\`\`
`;

    return formatted;
  }

  /**
   * Setup global error handlers
   */
  private setupGlobalErrorHandlers() {
    // Handle unhandled promise rejections
    window.addEventListener("unhandledrejection", (event) => {
      const error =
        event.reason instanceof Error
          ? event.reason
          : new Error(String(event.reason));

      this.logError(error, {
        type: "unhandledrejection",
        promise: event.promise,
      });
    });

    // Override console.error to add debugging format
    const originalConsoleError = console.error;
    console.error = (...args) => {
      originalConsoleError.apply(console, args);

      // Check if first argument is an Error
      if (args[0] instanceof Error) {
        this.logError(args[0], {
          type: "console.error",
          additionalArgs: args.slice(1),
        });
      }
    };
  }

  /**
   * Create a formatted error report that can be copied
   */
  createReport(error: Error, context?: ErrorContext): string {
    return this.formatError(error, context);
  }
}

// Export singleton instance
export const developmentErrorLogger = new DevelopmentErrorLogger();

// Export helper functions
export const logError = (error: Error, context?: ErrorContext) => {
  developmentErrorLogger.logError(error, context);
};

export const createErrorReport = (error: Error, context?: ErrorContext) => {
  return developmentErrorLogger.createReport(error, context);
};
